import { useActiveChild } from '../context/ActiveChildContext';
import { useAuth } from '../context/AuthContext';
import { Eye, X } from 'lucide-react';

/**
 * Sticky orange banner shown to parents on any child-scoped page.
 * Makes it impossible to forget WHICH child you're currently viewing
 * and reinforces that the parent is in read-only / observer mode.
 *
 * Renders nothing for non-parents or when no active child is selected.
 */
export default function ParentViewBanner({ pageLabel }) {
  const { user } = useAuth();
  const { activeChild, children, setActiveChildId } = useActiveChild();

  if (user?.role !== 'parent') return null;
  if (!activeChild) return null;

  const childName =
    activeChild.full_name || activeChild.name || activeChild.first_name || 'your child';
  const firstName = childName.split(' ')[0];

  // Some pages (Decision Lab, Calculator preview) are interactive on behalf of
  // the child rather than locked. The banner copy adapts so we don't lie about
  // "read-only" when the parent can actually click around.
  const INTERACTIVE_PAGES = new Set(['Decision Lab', 'Roadmap']);
  const isInteractive = INTERACTIVE_PAGES.has(pageLabel || '');
  const modeLabel = isInteractive
    ? `Choices won't save to ${firstName}'s plan`
    : 'Observer mode (read-only)';

  return (
    <div
      data-testid="parent-view-banner"
      className="mb-4 flex items-center gap-3 rounded-xl border border-blue-500/20 bg-[linear-gradient(90deg,rgba(30,58,138,0.45)_0%,rgba(67,56,202,0.40)_50%,rgba(30,58,138,0.45)_100%)] backdrop-blur-xl px-4 py-2.5 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_12px_32px_-16px_rgba(59,130,246,0.35)]"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/15">
        <Eye className="h-3.5 w-3.5 text-blue-100" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-blue-50">
          Viewing <span className="font-semibold text-white">{firstName}'s</span>
          {pageLabel ? <span className="text-blue-100/90"> {pageLabel}</span> : ' journey'}
          <span className="mx-2 text-blue-200/50">·</span>
          <span className="text-blue-100/80">{modeLabel}</span>
        </p>
      </div>
      {children.length > 1 && (
        <button
          type="button"
          onClick={() => {
            const ids = children.map(c => c.id || c.user_id);
            const cur = ids.indexOf(activeChild.id || activeChild.user_id);
            const next = ids[(cur + 1) % ids.length];
            if (next) setActiveChildId(next);
          }}
          data-testid="parent-view-banner-switch"
          className="hidden sm:inline-flex shrink-0 rounded-md bg-white/10 hover:bg-white/20 ring-1 ring-white/15 px-2.5 py-1 text-xs font-medium text-white transition-colors"
        >
          Switch child
        </button>
      )}
    </div>
  );
}
